import { useState } from "react";
import { Link } from "react-router-dom";
import {
  DownloadIcon,
  FilmIcon,
  FolderIcon,
  LockIcon,
  PlusIcon,
  Share2Icon,
  ShieldCheckIcon,
  Trash2Icon,
  UnlockIcon,
} from "lucide-react";

import { useCameras } from "@/api/cameras";
import {
  useCaseFolders,
  useCreateCase,
  useCreateEvidence,
  useDeleteEvidence,
  useEvidenceExports,
  useEvidenceList,
  useExportEvidence,
  useLockEvidence,
  useShareEvidence,
  useUnlockEvidence,
  type EvidenceItem,
  type EvidenceType,
} from "@/api/evidence";
import { useUsers } from "@/api/users";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CustodyPanel, type CustodyEvent } from "@/components/CustodyPanel";
import { shortId } from "@/types/deviceRef";
import type { TimestampInput } from "@/utils/datetime";
import { cn } from "@/lib/utils";

type Tab = "items" | "cases";

function StatusBadge({ status }: { status: string }) {
  const m: Record<string, "online" | "warning" | "error" | "offline"> = {
    pending: "warning",
    processing: "warning",
    ready: "online",
    failed: "error",
  };
  return <Badge variant={m[status] ?? "offline"}>{status}</Badge>;
}

function fmtDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function CreateEvidenceDialog({
  open, onClose,
}: { open: boolean; onClose: () => void }) {
  const { data: cams } = useCameras({ limit: 200 });
  const create = useCreateEvidence();
  const [cameraId, setCameraId] = useState("");
  const [evidenceType, setEvidenceType] = useState<EvidenceType>("clip");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startedAt, setStartedAt] = useState(() =>
    new Date(Date.now() - 5 * 60_000).toISOString().slice(0, 16),
  );
  const [endedAt, setEndedAt] = useState(() =>
    new Date().toISOString().slice(0, 16),
  );

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-lg">
        <CardContent className="space-y-3 p-4">
          <h3 className="text-lg font-semibold">Create evidence</h3>
          <div className="space-y-1">
            <Label>Camera</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={cameraId}
              onChange={(e) => setCameraId(e.target.value)}
            >
              <option value="">— pick —</option>
              {(cams?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Type</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={evidenceType}
              onChange={(e) => setEvidenceType(e.target.value as EvidenceType)}
            >
              <option value="clip">Clip</option>
              <option value="snapshot">Snapshot</option>
              <option value="bookmark">Bookmark</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>Started</Label>
              <Input
                type="datetime-local"
                value={startedAt}
                onChange={(e) => setStartedAt(e.target.value)}
              />
            </div>
            {evidenceType === "clip" && (
              <div className="space-y-1">
                <Label>Ended</Label>
                <Input
                  type="datetime-local"
                  value={endedAt}
                  onChange={(e) => setEndedAt(e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Label>Description</Label>
            <textarea
              className="flex w-full rounded-md border border-input bg-background p-2 text-sm"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              disabled={!cameraId || !title || create.isPending}
              onClick={async () => {
                await create.mutateAsync({
                  camera_id: cameraId,
                  title,
                  description: description || undefined,
                  evidence_type: evidenceType,
                  started_at: new Date(startedAt).toISOString(),
                  ended_at:
                    evidenceType === "clip"
                      ? new Date(endedAt).toISOString()
                      : undefined,
                });
                onClose();
              }}
            >Create</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ShareModal({ url, onClose }: { url: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-3 p-4">
          <h3 className="text-lg font-semibold">Share link</h3>
          <p className="text-xs text-amber-700">
            ⚠ Link expires in 24 hours.
          </p>
          <Input readOnly value={url} />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                await navigator.clipboard.writeText(url);
                setCopied(true);
              }}
            >{copied ? "Copied" : "Copy"}</Button>
            <Button size="sm" onClick={onClose}>Done</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Batch 1 (UX/IA) — chain-of-custody: owner + full export/access trail
// (who, from which IP, when). The data was captured server-side (created_by +
// evidence_exports) but was previously invisible in-app — a BFSI/STQC gap.
function CustodyModal({
  item, onClose,
}: {
  item: EvidenceItem | null;
  onClose: () => void;
}) {
  const exports = useEvidenceExports(item?.id ?? null);
  const users = useUsers();
  const resolveUser = (id: string | null | undefined) => {
    if (!id) return "";
    const u = (users.data?.items ?? []).find((x) => x.id === id);
    return u ? u.full_name || u.email : shortId(id);
  };
  if (!item) return null;

  const events: CustodyEvent[] = [
    {
      action: "Created",
      actor: resolveUser(item.created_by),
      at: item.created_at as TimestampInput,
    },
    // Chronological: the export log is newest-first, so reverse it.
    ...[...(exports.data?.items ?? [])].reverse().map((e) => ({
      action: e.shared ? "Share link issued" : "Exported",
      actor: resolveUser(e.exported_by),
      at: e.created_at as TimestampInput,
      ip: e.ip_address,
    })),
  ];
  if (item.locked) {
    events.push({ action: "Legal hold applied", reason: item.locked_reason });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-lg">
        <CardContent className="space-y-4 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Chain of custody</h3>
            <Badge variant={item.watermarked ? "online" : "warning"}>
              {item.watermarked ? "Watermarked" : "Not watermarked"}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{item.title}</p>
          <CustodyPanel
            title="Custody trail"
            events={events}
            emptyText="No custody events recorded yet."
          />
          <p className="text-[11px] text-muted-foreground">
            {item.export_count} export{item.export_count === 1 ? "" : "s"} recorded.
            Chain-of-custody is retained for audit.
          </p>
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function LockModal({
  item, onClose,
}: {
  item: EvidenceItem | null;
  onClose: () => void;
}) {
  const lock = useLockEvidence();
  const [reason, setReason] = useState("");
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-3 p-4">
          <h3 className="text-lg font-semibold">Apply legal hold</h3>
          <p className="text-sm text-muted-foreground">{item.title}</p>
          <Label>Reason</Label>
          <textarea
            className="flex w-full rounded-md border border-input bg-background p-2 text-sm"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this evidence must not be deleted…"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              disabled={!reason || lock.isPending}
              onClick={async () => {
                await lock.mutateAsync({ id: item.id, reason });
                onClose();
              }}
            >Lock</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EvidenceTab() {
  const { data, isLoading } = useEvidenceList();
  const unlock = useUnlockEvidence();
  const del = useDeleteEvidence();
  const exportFn = useExportEvidence();
  const share = useShareEvidence();
  const [createOpen, setCreateOpen] = useState(false);
  const [lockTarget, setLockTarget] = useState<EvidenceItem | null>(null);
  const [custodyTarget, setCustodyTarget] = useState<EvidenceItem | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  // Batch 1 (UX/IA) — watermark is now an explicit, visible choice, defaulting
  // ON (was silently hardcoded false). For a bank an export should be
  // tamper-evident unless deliberately opted out.
  const [watermark, setWatermark] = useState(true);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={watermark}
            onChange={(e) => setWatermark(e.target.checked)}
          />
          Watermark exports
        </label>
        <Button onClick={() => setCreateOpen(true)}>
          <PlusIcon className="mr-2 h-4 w-4" />Create evidence
        </Button>
      </div>

      {isLoading && <p className="text-sm">Loading…</p>}

      {data && data.items.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No evidence items yet.
          </CardContent>
        </Card>
      )}

      {data && data.items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Duration</th>
                  <th className="px-3 py-2 text-left">Exports</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => (
                  <tr key={it.id} className="border-b last:border-0">
                    <td className="px-3 py-2"><Badge>{it.evidence_type}</Badge></td>
                    <td className="px-3 py-2 font-medium">
                      <span className="flex items-center gap-1">
                        {it.locked && <LockIcon className="h-3.5 w-3.5 text-amber-600" />}
                        {it.title}
                      </span>
                    </td>
                    <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
                    <td className="px-3 py-2 text-xs">{fmtDuration(it.duration_seconds)}</td>
                    <td className="px-3 py-2 text-xs">{it.export_count}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm" variant="outline"
                          title={watermark ? "Export (watermarked)" : "Export (no watermark)"}
                          onClick={async () => {
                            const out = await exportFn.mutateAsync({ id: it.id, watermark });
                            window.open(out.download_url, "_blank");
                          }}
                        >
                          <DownloadIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          title="Chain of custody"
                          onClick={() => setCustodyTarget(it)}
                        >
                          <ShieldCheckIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={async () => {
                            const out = await share.mutateAsync({
                              id: it.id, expires_in_hours: 24,
                            });
                            setShareUrl(out.share_url);
                          }}
                        >
                          <Share2Icon className="h-3.5 w-3.5" />
                        </Button>
                        {!it.locked ? (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setLockTarget(it)}
                          >
                            <LockIcon className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => unlock.mutate(it.id)}
                          >
                            <UnlockIcon className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          size="sm" variant="outline"
                          disabled={it.locked}
                          onClick={async () => {
                            if (!confirm(`Delete "${it.title}"?`)) return;
                            await del.mutateAsync(it.id);
                          }}
                        >
                          <Trash2Icon className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <CreateEvidenceDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <LockModal item={lockTarget} onClose={() => setLockTarget(null)} />
      <CustodyModal item={custodyTarget} onClose={() => setCustodyTarget(null)} />
      {shareUrl && <ShareModal url={shareUrl} onClose={() => setShareUrl(null)} />}
    </div>
  );
}

function CreateCaseDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateCase();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-3 p-4">
          <h3 className="text-lg font-semibold">New case folder</h3>
          <Label>Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          <Label>Description</Label>
          <textarea
            className="flex w-full rounded-md border border-input bg-background p-2 text-sm"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              disabled={!title || create.isPending}
              onClick={async () => {
                await create.mutateAsync({
                  title, description: description || undefined,
                });
                onClose();
              }}
            >Create</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CasesTab() {
  const { data, isLoading } = useCaseFolders();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)}>
          <PlusIcon className="mr-2 h-4 w-4" />Create case
        </Button>
      </div>
      {isLoading && <p className="text-sm">Loading…</p>}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {(data?.items ?? []).map((c) => (
          <Card key={c.id}>
            <CardContent className="space-y-2 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold">{c.title}</p>
                  <p className="text-xs text-muted-foreground">{c.description ?? "—"}</p>
                </div>
                <Badge variant={c.status === "open" ? "online" : "offline"}>
                  {c.status}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {c.item_count} item{c.item_count === 1 ? "" : "s"}
              </p>
              <Button asChild size="sm" variant="outline">
                <Link to={`/evidence/cases/${c.id}`}>Open</Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
      {data && data.items.length === 0 && !isLoading && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No case folders yet.
          </CardContent>
        </Card>
      )}
      <CreateCaseDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

export default function EvidenceCenter() {
  const [tab, setTab] = useState<Tab>("items");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <FilmIcon className="h-6 w-6" />
          Evidence Center
        </h1>
        <p className="text-sm text-muted-foreground">
          Clips, snapshots, bookmarks, and investigation case folders.
        </p>
      </div>
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setTab("items")}
          className={cn(
            "px-3 py-2 text-sm",
            tab === "items"
              ? "border-b-2 border-primary font-medium text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >Evidence items</button>
        <button
          onClick={() => setTab("cases")}
          className={cn(
            "flex items-center gap-1 px-3 py-2 text-sm",
            tab === "cases"
              ? "border-b-2 border-primary font-medium text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        ><FolderIcon className="h-4 w-4" />Case folders</button>
      </div>
      {tab === "items" && <EvidenceTab />}
      {tab === "cases" && <CasesTab />}
    </div>
  );
}
